import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CreditCounter } from './CreditComponents';
import logo from '../assets/logo.png';

const ToolHub = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const tools = [
    {
      name: 'Job Tracker',
      description: 'Track all your applications in one place—from any platform',
      icon: '📋',
      link: '/tracker',
      badge: 'Free',
      badgeColor: 'bg-green-100 text-green-700',
      highlight: true
    },
    {
      name: 'Match Score',
      description: 'See how well your resume matches a job description',
      icon: '🎯',
      link: '/match',
      badge: '1 Credit',
      badgeColor: 'bg-blue-100 text-blue-700',
      highlight: false
    },
    {
      name: 'Future-Proof Audit',
      description: 'Discover your transferable strengths and explore exciting new career paths',
      icon: '🚀',
      link: '/tools/future-proof',
      badge: '5 Credits',
      originalBadge: '8 Credits',
      badgeColor: 'bg-purple-100 text-purple-700',
      highlight: true,
      isNew: true
    },
    {
      name: 'Cover Letter Starter',
      description: 'Generate a personalized cover letter intro in seconds',
      icon: '✍️',
      link: '/tools/cover-letter',
      badge: 'Free',
      badgeColor: 'bg-green-100 text-green-700',
      highlight: false
    },
    {
      name: 'Mock Interview',
      description: 'Practice with AI-generated interview questions for your target role',
      icon: '🎤',
      link: '/tools/mock-interview',
      badge: '1 Credit',
      badgeColor: 'bg-blue-100 text-blue-700',
      highlight: false
    },
    {
      name: 'The AlignifyNOW Reset!',
      description: 'Free checklist to reset your job search mindset',
      icon: '🌱',
      link: '/checklist',
      badge: 'Free Download',
      badgeColor: 'bg-purple-100 text-purple-700',
      highlight: false
    },
    {
      name: 'Blog',
      description: 'Tips, strategies, and insights for your job search',
      icon: '📚',
      link: '/blog',
      badge: 'Free',
      badgeColor: 'bg-green-100 text-green-700',
      highlight: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation - Simplified */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="AlignifyNOW" className="h-[60px] w-auto" />
              <span className="text-xl font-bold tracking-tight text-slate-900 hidden sm:inline">
                Alignify<span className="text-blue-600">NOW</span>
              </span>
            </Link>
            
            {/* Desktop Navigation - Simplified to 4 items */}
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-sm font-medium text-blue-600 border-b-2 border-blue-600 pb-1">
                Tool Hub
              </Link>
              <Link to="/tracker" className="text-sm font-medium text-gray-600 hover:text-gray-900">
                Job Tracker
              </Link>
              <Link to="/pricing" className="text-sm font-medium text-gray-600 hover:text-gray-900">
                Pricing
              </Link>
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  data-testid="nav-admin-link-desktop"
                  className="text-sm font-medium text-purple-700 hover:text-purple-900 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Admin
                </Link>
              )}
              
              <CreditCounter />
              
              {user && (
                <div className="flex items-center gap-2 pl-4 border-l border-gray-200">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-gray-200" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                      {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="text-sm font-medium text-gray-700">
                    {user.name?.split(' ')[0] || 'Account'}
                  </span>
                  <button
                    onClick={logout}
                    className="text-xs text-gray-500 hover:text-red-600 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
            
            {/* Mobile: Credits + Menu Button */}
            <div className="flex md:hidden items-center gap-3">
              <CreditCounter />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t py-4 space-y-1">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-3 text-base font-medium text-blue-600 bg-blue-50 rounded-lg">
                🏠 Tool Hub
              </Link>
              <Link to="/tracker" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                📋 Job Tracker
              </Link>
              <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                💰 Pricing
              </Link>
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  data-testid="nav-admin-link-mobile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-3 text-base font-medium text-purple-700 hover:bg-purple-50 rounded-lg"
                >
                  🛡️ Admin
                </Link>
              )}
              {user && (
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center gap-3 px-3 py-2">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full border border-gray-200" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                        {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{user.name || user.email}</p>
                      <button
                        onClick={() => { logout(); setMobileMenuOpen(false); }}
                        className="text-sm text-red-600"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Welcome Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}! 👋
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            What would you like to work on today?
          </p>
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {tools.map((tool) => (
            <Link
              key={tool.name}
              to={tool.link}
              className={`group relative bg-white rounded-2xl p-5 sm:p-6 shadow-sm border-2 transition-all duration-200 hover:shadow-lg hover:border-blue-200 active:scale-[0.98] ${
                tool.highlight ? 'border-blue-200 ring-2 ring-blue-100' : 'border-gray-100'
              }`}
            >
              {/* NEW Badge */}
              {tool.isNew && (
                <span className="absolute -top-2 -left-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                  NEW
                </span>
              )}
              
              {/* Credit Badge */}
              <span className={`absolute top-3 sm:top-4 right-3 sm:right-4 text-xs sm:text-sm font-medium px-2 sm:px-2.5 py-1 rounded-full ${tool.badgeColor} flex items-center gap-1`}>
                {tool.originalBadge && (
                  <span className="text-gray-400 line-through font-normal">{tool.originalBadge}</span>
                )}
                <span>{tool.badge}</span>
              </span>
              
              {/* Icon */}
              <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">{tool.icon}</div>
              
              {/* Content */}
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2 group-hover:text-blue-600 transition-colors">
                {tool.name}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                {tool.description}
              </p>
              
              {/* Arrow indicator */}
              <div className="mt-4 text-blue-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                Open →
              </div>
            </Link>
          ))}
        </div>

        {/* Quick Stats / Tips Section */}
        <div className="mt-12 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">💡 Quick Tip</h2>
          <p className="text-gray-600">
            Start with the <strong>Match Score</strong> tool to see how well your resume aligns with a job before applying. 
            Then use the <strong>Job Tracker</strong> to keep everything organized. Most successful job seekers track 
            their applications and follow up consistently!
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <span className="text-lg font-bold text-white opacity-80">
                Alignify<span className="text-blue-400">NOW</span>
              </span>
            </div>
            <div className="text-gray-400 text-xs mb-4 md:mb-0">
              © 2026 AlignifyNOW. All rights reserved. Built by Matambanadzo Studio.
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/affiliates" className="text-gray-400 hover:text-white text-sm transition-colors">
                Affiliates
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ToolHub;
